import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilAltIcon, TrashIcon } from '@heroicons/react/solid';
import DeleteConfirmModal from '../../../common/DeleteConfirmModal';
import AddCategoryModal from './components/AddCategoryModal';
import EditCategoryModal from './components/EditCategoryModal';
import { toastService } from '../../../../services/toast';
import { api } from '../../../../services/api';
import { LoadingSpinner } from '../../../common/LoadingSpinner';
import { usePermissions } from '../../../../contexts/PermissionsContext';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastCategoryCount, setLastCategoryCount] = useState(3);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { hasPermission } = usePermissions();

  // جلب التصنيفات
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const data = await api.categories.getAll();
      setCategories(data);
      setLastCategoryCount(data.length || 3);
    } catch (error) {
      if (error.response?.status === 429) {
        toastService.error('تم تجاوز عدد الطلبات المسموح به، يرجى المحاولة لاحقاً');
      } else {
        toastService.error('حدث خطأ أثناء جلب التصنيفات');
      }
      console.error('Error fetching categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async (formData) => {
    try {
      await api.categories.create(formData);
      toastService.success('تم إضافة التصنيف بنجاح');
      fetchCategories();
      setShowAddModal(false);
    } catch (error) {
      toastService.error('حدث خطأ أثناء إضافة التصنيف');
      console.error('Error adding category:', error);
    }
  };

  const handleEdit = async (formData) => {
    try {
      await api.categories.update(selectedCategory.id, formData);
      toastService.success('تم تعديل التصنيف بنجاح');
      fetchCategories();
      setShowEditModal(false);
      setSelectedCategory(null);
    } catch (error) {
      toastService.error('حدث خطأ أثناء تعديل التصنيف');
      console.error('Error updating category:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await api.categories.delete(selectedCategory.id);
      toastService.success('تم حذف التصنيف بنجاح');
      fetchCategories();
      setShowDeleteModal(false);
      setTimeout(() => {
        setSelectedCategory(null);
      }, 200);
    } catch (error) {
      if (error.response?.status === 422) {
        toastService.error(error.response.data.message);
      } else {
        toastService.error('حدث خطأ أثناء حذف التصنيف');
      }
      console.error('Error deleting category:', error);
    }
  };

  // تصفية التصنيفات بناءً على البحث
  const filteredCategories = categories.filter(category =>
    searchQuery === '' ||
    category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="p-6">
      {/* بطاقة العنوان والبحث والأيقونات */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">قائمة التصنيفات</h2>
          <div className="flex gap-4">
            {hasPermission('create_category') && (
              <div className="relative group overflow-visible">
                <PlusIcon 
                  className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors duration-200"
                  onClick={() => setShowAddModal(true)}
                  aria-hidden="true"
                />
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                  إضافة تصنيف جديد
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="p-6">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">البحث عن تصنيف</label>
            <div className="relative">
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-blue-500 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="البحث عن تصنيف..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-base pr-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* بطاقة جدول التصنيفات */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        {isLoading ? (
          <div className="overflow-x-auto w-full">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-right">
                    <span className="animate-pulse block h-5 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-right">
                    <span className="animate-pulse block h-5 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-right w-32">
                    <span className="animate-pulse block h-5 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-right w-24">
                    <span className="animate-pulse block h-5 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {[...Array(lastCategoryCount)].map((_, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <div className="animate-pulse h-5 w-10 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-end space-x-3 space-x-reverse">
                        <div className="animate-pulse h-5 w-5 bg-gray-300 dark:bg-gray-600 rounded-md"></div>
                        <div className="animate-pulse h-5 w-5 bg-gray-300 dark:bg-gray-600 rounded-md"></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    اسم التصنيف
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    الوصف
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                    عدد الدورات
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredCategories.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      لا توجد تصنيفات حالياً
                    </td>
                  </tr>
                ) : (
                  filteredCategories.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {category.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300">
                        {category.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {category.courses_count || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        <div className="flex items-center justify-end space-x-3 space-x-reverse">
                          {hasPermission('edit_category') && (
                            <button
                              onClick={() => {
                                setSelectedCategory(category);
                                setShowEditModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <PencilAltIcon className="h-5 w-5" />
                            </button>
                          )}
                          {hasPermission('delete_category') && (
                            <button
                              onClick={() => {
                                if (category.courses_count > 0) {
                                  toastService.error(`لا يمكن حذف التصنيف "${category.name}" لأنه يحتوي على ${category.courses_count} دورة نشطة`);
                                  return;
                                }
                                setSelectedCategory(category);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddCategoryModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAdd}
      />

      <EditCategoryModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setTimeout(() => {
            setSelectedCategory(null);
          }, 200);
        }}
        onSubmit={handleEdit}
        category={selectedCategory}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTimeout(() => {
            setSelectedCategory(null);
          }, 200);
        }}
        onConfirm={handleDelete}
        itemType="التصنيف"
        itemTitle={selectedCategory?.name}
      />
    </div>
  );
};

export default React.memo(CategoriesPage);
